import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {Observable, Subject, Subscription} from "rxjs";
import { debounceTime, distinctUntilChanged, map, startWith } from "rxjs/operators";
import { FormControl, NgModel } from "@angular/forms";
import { SelectOption } from "../../classes/select-option";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";

@Component({
  selector: 'app-weave-input-autocomplete',
  templateUrl: './weave-input-autocomplete.component.html',
  styleUrl: './weave-input-autocomplete.component.scss',
  standalone: false
})
export class WeaveInputAutocompleteComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() data: string;
  @Input() type: string = "text";
  @Input() disabledFlag: boolean;
  @Input() appearance: 'outline' | 'fill' | 'standard' = "outline"; // 'outline' | 'fill' | 'standard'
  @Input() label: string = "";
  @Input() description: string = "";
  @Input() placeholder: string = "";
  @Input() maxlength: number = 524288;
  @Input() required: boolean = false;
  @Input() longTitle: boolean = false;
  @Input() emailValidator: boolean = false;
  @Input() showError: boolean = false;
  @Input() hideRequiredMarker: boolean = false;
  @Input() autocompleteItems: SelectOption[] = [];
  @Output() focusOut: EventEmitter<SelectOption> = new EventEmitter<SelectOption>();
  @Output() keyUp: EventEmitter<string> = new EventEmitter<string>();
  @Output() keyUpChar: EventEmitter<string> = new EventEmitter<string>();
  @Output() keyDown: EventEmitter<string> = new EventEmitter<string>();
  @Output() keyUpDebounced: EventEmitter<string> = new EventEmitter<string>();
  @Output() inputIsValid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() keyUpEnter: EventEmitter<void> = new EventEmitter<void>();
  @Output() paste: EventEmitter<ClipboardEvent> = new EventEmitter<ClipboardEvent>();

  tempValue: any;
  id: string = "";
  message: string = "";

  filteredOptions: Observable<SelectOption[]>;
  itemSelected: SelectOption = { label: '', value: '' };

  searchItemsCtrl = new FormControl<string>('');

  private keyUpDebouncedSubject  = new Subject<string>();
  private keyUpDebouncedSubscription: Subscription;
  @ViewChild('ctrl') field: NgModel;
  @ViewChild('inputElement') inputElement: ElementRef<HTMLInputElement>;

  constructor() {}

  ngOnInit() {
    this.id = "weave-input" + Math.floor(Math.random() * 1000);

    this.filteredOptions = this.searchItemsCtrl.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '')),
    );

    this.keyUpDebouncedSubscription = this.keyUpDebouncedSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe((results: string) => (this.keyUpDebounced.emit(results)));

    this.message = this.label;
    if( this.message.length > 30 || this.message.length == 0 ) {
      this.message = "This field";
    }
  }

  private _filter(value: string): SelectOption[] {
    const filterValue = value.toLowerCase();

    return this.autocompleteItems.filter(option => option.label.toLowerCase().includes(filterValue));
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if(this.field) {
        this.inputIsValid.emit(this.field.valid);
      }
    }, 50);
  }

  ngOnDestroy() {
    this.keyUpDebouncedSubscription.unsubscribe();
  }

  onKeyUpEnter() {
    this.keyUpEnter.emit();
  }

  onFocusOut() {
    this.data = this.searchItemsCtrl.value;
    this.showError = false;
    //check if text was entered after a selection
    if(this.itemSelected.label !== this.data) {
      this.itemSelected = {
        label: this.data,
        value: ""
      };
    }
    this.focusOut.emit(this.itemSelected);
  }
  onKeyUp(event: Event) {
    this.keyUp.emit(this.data);
    if(this.field){
      this.inputIsValid.emit(this.field.valid);
    }
    this.keyUpDebouncedSubject.next(this.data);
    this.keyUpChar.emit((event as KeyboardEvent).key);
  }

  onKeyDown(event: Event) {
    this.keyDown.emit((event as KeyboardEvent).key);
  }
  onSelected(event: MatAutocompleteSelectedEvent) {
    this.itemSelected = {
      label: event.option.viewValue,
      value: event.option.value
    };
    this.searchItemsCtrl.setValue(this.itemSelected.label);
    this.focusOut.emit(this.itemSelected);
  }

  startEdit() {
    this.tempValue = this.data;
  }

  onPaste(event: ClipboardEvent) {
    this.paste.emit(event);
  }

  public focusInput() {
    this.inputElement.nativeElement.focus();
  }

  public selectInput() {
    this.inputElement.nativeElement.select();
  }

  undo() {
    this.data = this.tempValue;
  }
}
